import { formatDateTime } from '../../lib/utils';
/**
 * Device Investigation Card
 * 
 * Displays device details including compliance and management status.
 */

import {
  Monitor,
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  Building2,
  Cpu,
  HardDrive,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import type { DeviceInvestigationResult } from '../../data/types';

interface DeviceInvestigationCardProps {
  result: DeviceInvestigationResult;
}

export function DeviceInvestigationCard({ result }: DeviceInvestigationCardProps) {
  const { device, cachedAt } = result;

  return (
    <div className="space-y-4">
      {/* Device Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5 text-accent-blue" />
              Device Information
            </CardTitle>
            <div className="flex items-center gap-2">
              {device.isCompliant !== null && (
                device.isCompliant ? (
                  <Badge variant="green">
                    <CheckCircle2 className="w-3 h-3" />
                    Compliant
                  </Badge>
                ) : (
                  <Badge variant="orange">
                    <XCircle className="w-3 h-3" />
                    Non-Compliant
                  </Badge>
                )
              )}
              {device.isManaged !== null && (
                device.isManaged ? (
                  <Badge variant="blue">
                    <Shield className="w-3 h-3" />
                    Managed
                  </Badge>
                ) : (
                  <Badge variant="default">
                    Unmanaged
                  </Badge>
                )
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Device Name and ID */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-accent-blue/20 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-accent-blue" />
              </div>
              <div>
                <p className="text-lg font-medium text-text-primary">{device.displayName}</p>
                <p className="text-sm text-text-secondary font-mono">{device.deviceId}</p>
              </div>
            </div>

            {/* Device Details Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border-default">
              {/* Operating System */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-text-tertiary">
                  <Cpu className="w-4 h-4" />
                  Operating System
                </div>
                <p className="text-sm text-text-primary font-medium">
                  {device.operatingSystem}
                </p>
                {device.operatingSystemVersion && (
                  <p className="text-xs text-text-secondary font-mono">
                    Version: {device.operatingSystemVersion}
                  </p>
                )}
              </div>

              {/* Trust Type */}
              {device.trustType && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-text-tertiary">
                    <Shield className="w-4 h-4" />
                    Trust Type
                  </div>
                  <p className="text-sm text-text-primary font-medium capitalize">
                    {device.trustType.replace(/([A-Z])/g, ' $1').trim()}
                  </p>
                </div>
              )}

              {/* Manufacturer */}
              {device.manufacturer && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-text-tertiary">
                    <Building2 className="w-4 h-4" />
                    Manufacturer
                  </div>
                  <p className="text-sm text-text-primary font-medium">
                    {device.manufacturer}
                  </p>
                </div>
              )}

              {/* Model */}
              {device.model && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-text-tertiary">
                    <HardDrive className="w-4 h-4" />
                    Model
                  </div>
                  <p className="text-sm text-text-primary font-medium">
                    {device.model}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-accent-blue" />
            Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Last Sign-In */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Last Sign-In</span>
              <span className="text-sm text-text-primary">
                {device.approximateLastSignInDateTime
                  ? formatDateTime(device.approximateLastSignInDateTime)
                  : 'Unknown'}
              </span>
            </div>

            {/* Days Since Last Sign-In */}
            {device.approximateLastSignInDateTime && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Days Since Sign-In</span>
                <span className="text-sm text-text-primary">
                  {getDaysSince(device.approximateLastSignInDateTime)} days
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent-blue" />
            Compliance & Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Compliance Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Compliance Status</span>
              <div className="flex items-center gap-2">
                {device.isCompliant === null ? (
                  <span className="text-sm text-text-tertiary">Unknown</span>
                ) : device.isCompliant ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-accent-green" />
                    <span className="text-sm text-accent-green">Compliant</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-accent-orange" />
                    <span className="text-sm text-accent-orange">Non-Compliant</span>
                  </>
                )}
              </div>
            </div>

            {/* Management Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Management Status</span>
              <div className="flex items-center gap-2">
                {device.isManaged === null ? (
                  <span className="text-sm text-text-tertiary">Unknown</span>
                ) : device.isManaged ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-accent-green" />
                    <span className="text-sm text-accent-green">Managed</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-text-tertiary" />
                    <span className="text-sm text-text-secondary">Unmanaged</span>
                  </>
                )}
              </div>
            </div>

            {/* Trust Type */}
            {device.trustType && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Trust Type</span>
                <span className="text-sm text-text-primary capitalize">
                  {formatTrustType(device.trustType)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cache Info */}
      <p className="text-xs text-text-tertiary text-center">
        Data cached at {formatDateTime(cachedAt)}
      </p>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function getDaysSince(dateString: string): number {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function formatTrustType(trustType: string): string {
  const trustTypeLabels: Record<string, string> = {
    AzureAd: 'Azure AD Joined',
    ServerAd: 'Domain Joined (Hybrid)',
    Workplace: 'Workplace Joined',
  };
  return trustTypeLabels[trustType] || trustType.replace(/([A-Z])/g, ' $1').trim();
}
